import React, { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { DropdownPanel } from './DropdownPanel';
import { useDismiss } from '../hooks/useDismiss';
import { dialCodes } from '../data/dialCodes';
type DialCodeSelectProps = {
  id: string;
  value: string;
  onChange: (code: string) => void;
  className?: string;
};
export function DialCodeSelect({
  id,
  value,
  onChange,
  className = 'pb-3 pt-1.5 text-[18px]'
}: DialCodeSelectProps) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const [customError, setCustomError] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  const known = dialCodes.some(d => d.code === value);
  const choose = (code: string) => {
    onChange(code);
    setOpen(false);
  };
  const applyCustom = () => {
    const digits = custom.replace(/[^\d]/g, '');
    if (digits.length < 1 || digits.length > 4) {
      setCustomError('Enter 1–4 digits');
      return;
    }
    setCustom('');
    setCustomError('');
    choose(`+${digits}`);
  };
  return <div ref={ref} className="relative shrink-0" data-id="element-386" data-mp="1pjbkux1ix0uc">
      <button id={id} type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={`Country code, ${value}`} onClick={() => setOpen(o => !o)} className={`flex items-center gap-1.5 bg-transparent focus:outline-none focus-visible:underline ${className}`} data-id="element-387" data-mp="1pi0847q3oir3">
        <span data-id="element-388" data-mp="rxnr6jwgcwmi">{value}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 text-ink/60 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-389" data-mp="16sk6fh2oik93" />
      </button>

      <DropdownPanel open={open} className="left-0 w-[300px]" data-id="element-390" data-mp="2i6xou6eny7hq">
        <ul role="listbox" aria-label="Country code" className="max-h-[300px] overflow-y-auto py-2" data-id="element-391" data-mp="2d8w31dpuru2k">
          {!known && <DialRow label="Your entry" code={value} selected onSelect={() => choose(value)} data-id="element-392" data-mp="2kcvmcmtai5l5" />}
          {dialCodes.map(d => <DialRow key={d.iso} label={d.country} code={d.code} selected={d.code === value} onSelect={() => choose(d.code)} data-id="element-393" data-mp="2p9cdzqzp722z" />)}
        </ul>
        <div className="border-t border-line px-5 py-4" data-id="element-394" data-mp="2z9mpn76341g1">
          <label htmlFor={`${id}-custom`} className="text-[13px] font-light text-ink/70" data-id="element-395" data-mp="2z70ih8n3vnyc">
            Another country code
          </label>
          <div className="mt-2 flex items-end gap-3" data-id="element-396" data-mp="26jw9kskw6pha">
            <div className="flex flex-1 items-end border-b border-ink" data-id="element-397" data-mp="26il2ais8mzlf">
              <span className="pb-1.5 text-[16px]" data-id="element-398" data-mp="2bi7jf3b6szzi">+</span>
              <input id={`${id}-custom`} type="text" inputMode="numeric" value={custom} onChange={e => {
              setCustom(e.target.value.replace(/[^\d]/g, '').slice(0, 4));
              if (customError) setCustomError('');
            }} onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyCustom();
              }
            }} placeholder="e.g. 230" className="min-w-0 flex-1 bg-transparent pb-1.5 pl-0.5 text-[16px] placeholder:font-light placeholder:text-ink/50 focus:outline-none" data-id="element-399" data-mp="2gb0o5r6no8bl" />
            </div>
            <button type="button" onClick={applyCustom} className="bg-ink px-4 py-2 text-[14px] font-medium text-paper transition-opacity duration-200 hover:opacity-85" data-id="element-400" data-mp="3i6wp2a61n43i">
              Use
            </button>
          </div>
          {customError && <p className="mt-2 text-[13px] text-ink/70" data-id="element-401" data-mp="34g1k38yo1757">{customError}</p>}
        </div>
      </DropdownPanel>
    </div>;
}
type DialRowProps = {
  label: string;
  code: string;
  selected: boolean;
  onSelect: () => void;
};
function DialRow({
  label,
  code,
  selected,
  onSelect
}: DialRowProps) {
  return <li role="option" aria-selected={selected} data-id="element-402" data-mp="ydo3ddrmy0f5">
      <button type="button" onClick={onSelect} className={`flex w-full items-center gap-3 px-5 py-2.5 text-left text-[15px] transition-colors duration-200 hover:bg-bone ${selected ? 'font-medium' : 'font-light'}`} data-id="element-403" data-mp="13ciju924rcw3">
        <span className="flex-1" data-id="element-404" data-mp="tjtb3lxysd4g">{label}</span>
        <span className="tabular-nums text-ink/70" data-id="element-405" data-mp="fld6c0jnwwoy">{code}</span>
        <span className="w-4" data-id="element-406" data-mp="klhpxzfva7je">{selected && <CheckIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-407" data-mp="kmj0m2sz0s0l" />}</span>
      </button>
    </li>;
}